/**
 * Upload Page: drag & drop / browse, video preview, upload progress,
 * and an Analyze button that kicks off the backend pipeline.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DropZone from '../components/DropZone';
import VideoPreview from '../components/VideoPreview';
import { uploadVideo, startAnalysis } from '../api/api';

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleFileSelected = (selected) => {
    setFile(selected);
    setUploadProgress(0);
    setError('');
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setIsUploading(true);
    setError('');
    try {
      const { data } = await uploadVideo(file, setUploadProgress);
      await startAnalysis(data.jobId);
      navigate(`/processing/${data.jobId}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
      setIsUploading(false);
    }
  };

  return (
    <div>
      <section className="bg-ink-950 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-300 bg-brand-500/10 border border-brand-400/20 rounded-full px-3 py-1 mb-5">
            Automated Video Intelligence
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Understand any video, <span className="text-brand-400">automatically</span>
          </h1>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto leading-relaxed">
            Upload a video and our engine analyzes every frame, transcribes the audio, and
            reconstructs a complete, structured report — scenes, people, objects, dialogue,
            and key events, all with searchable timestamps.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mt-8 text-sm">
            {[
              ['Frame-by-frame scene detection'],
              ['Full audio transcription'],
              ['Timestamped event timeline'],
              ['Downloadable reports'],
            ].map(([label]) => (
              <span
                key={label}
                className="flex items-center gap-2 bg-white/5 border border-white/10 text-slate-300 rounded-full px-3.5 py-1.5"
              >
                <svg viewBox="0 0 20 20" fill="none" className="w-3.5 h-3.5 text-brand-400">
                  <path d="m4 10.5 3.5 3.5L16 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-4 -mt-10 pb-16">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-card p-6 sm:p-8">
          {!file && <DropZone onFileSelected={handleFileSelected} />}

          {file && (
            <div className="space-y-4">
              <VideoPreview file={file} />

              {isUploading && (
                <div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-brand-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Uploading… {uploadProgress}%</p>
                </div>
              )}

              {error && (
                <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleAnalyze}
                  disabled={isUploading}
                  className="bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-medium px-5 py-2.5 rounded-lg shadow-soft transition-colors"
                >
                  {isUploading ? 'Uploading…' : 'Analyze Video'}
                </button>
                <button
                  onClick={() => setFile(null)}
                  disabled={isUploading}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Choose Different File
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
